import api from '../util/api';
import moment from 'moment';
import { popupErrorFromServer } from '../util/helperFunctions';
import Location from './Location';
import * as Sentry from '@sentry/browser';

class Animal implements Ianimal {
  aNumber: number;
  name: string;
  active: boolean;
  animalType: 'cat' | 'dog';
  animalOrigin: string | undefined;
  assessmentStatus: 'initial' | 'full' | 'complete' | undefined;
  breed: string;
  colorCode: string;
  created: string;
  previousEnrichment?: Enrichment;
  lastCountableEnrichment?: Enrichment;
  arrivalDate: string;
  location: ILocation | null;
  nextAssessmentDue: string | undefined;
  note: string;
  prefered: boolean;
  signOutData: SignOutData | null;
  specialNeeds: SpecialNeed[];
  trainingPlan: 'yes' | 'no';
  isNew: boolean;
  _evaluations?: Evaluation[];
  sortOverride?: boolean;
  medicalTag: 'red' | 'green' | null;

  private _location?: Location;

  get evaluations() {
    return this._evaluations ? this._evaluations : [];
  }

  get locationEntity() {
    if (!this._location) {
      this._location = new Location(this.location);
    }
    return this._location;
  }

  get tagOrdinal() {
    switch (this.medicalTag) {
      case 'green':
        return -1;
      case 'red':
        return 1;
      default:
        return 0;
    }
  }

  getLocationName() {
    if (this.location && this.location.name) {
      return this.location.name;
    }

    if (this.location) {
      throw new Error(`Missing location object: ${this.location}`);
    }

    return 'unknown';
  }

  hasMorningEvaluation() {
    if (!this._evaluations) {
      console.warn('Animal does not have evaluations registered', this);
    }
    return this.evaluations.filter(evaluation => evaluation.time === 'AM').length > 0;
  }

  hasEveningEvaluation() {
    if (!this._evaluations) {
      console.warn('Animal does not have evaluations registered', this);
    }
    return this.evaluations.filter(evaluation => evaluation.time === 'PM').length > 0;
  }

  constructor(animal: Ianimal, isNew = false) {
    this.aNumber = parseInt(animal.aNumber.toString(), 10);
    this.name = animal.name;
    this.active = animal.active;
    this.animalType = animal.animalType;
    this.animalOrigin = animal.animalOrigin;
    this.assessmentStatus = animal.assessmentStatus;
    this.breed = animal.breed;
    this.colorCode = animal.colorCode;
    this.created = animal.created;
    this.previousEnrichment = animal.previousEnrichment;
    this.lastCountableEnrichment = animal.lastCountableEnrichment;
    this.arrivalDate = animal.arrivalDate;
    this.location = animal.location;
    this.nextAssessmentDue = animal.nextAssessmentDue;
    this.note = animal.note;
    this.prefered = animal.prefered;
    this.signOutData = animal.signOutData;
    this.specialNeeds = animal.specialNeeds;
    this.trainingPlan = animal.trainingPlan;
    this.isNew = isNew;
    this.medicalTag = animal.medicalTag;
  }

  get isLongStay() {
    const startDate = moment(this.arrivalDate);
    const currentDate = moment();
    const difference = currentDate.diff(startDate, 'days');
    return difference >= 30;
  }

  addSentryBreadcrumb = () => {
    Sentry.addBreadcrumb({
      category: 'ANIMAL_SAVE',
      message: `${this.name.toUpperCase().trim()}_SAVE`,
      level: Sentry.Severity.Info,
      data: {
        animal: JSON.stringify(this),
      },
    });
  };

  async save() {
    this.addSentryBreadcrumb();
    if (this.isNew) {
      const { data } = await api.animals.post(this);
      return data;
    }

    const { data } = await api.animals.patch(this.aNumber, this);
    return data;
  }

  async toggleActive() {
    this.active = !this.active;
    return await this.save();
  }

  async delete() {
    try {
      const { data } = await api.animals.delete(this.aNumber);
      return data;
    } catch (err) {
      popupErrorFromServer(err);
    }
  }

  clone() {
    return new Animal(this, this.isNew);
  }

  findAndRegisterEvaluations(evaluationsForAllAnimals: Evaluation[]) {
    this._evaluations = evaluationsForAllAnimals.filter(evaluation => evaluation.aNumber === this.aNumber);
  }

  isDog() {
    return this.animalType === 'dog';
  }

  isCat() {
    return this.animalType === 'cat';
  }

  hasMedicalTag() {
    return Boolean(this.medicalTag);
  }

  static blank() {
    return new Animal(
      {
        aNumber: 0.0,
        name: '',
        active: true,
        animalType: 'dog',
        animalOrigin: undefined,
        assessmentStatus: 'complete',
        nextAssessmentDue: undefined,
        breed: '',
        colorCode: '',
        created: moment().toISOString(),
        previousEnrichment: undefined,
        arrivalDate: moment().toISOString(),
        location: null,
        note: '',
        prefered: false,
        signOutData: null,
        specialNeeds: [],
        trainingPlan: 'no',
        medicalTag: null,
      },
      true,
    );
  }
}

export default Animal;
